import {formatTemp} from './formatters.js';
import { EARLY_MORNING_CUTOFF_HOUR } from '../constants/app.js';

/**
 * Draws a smooth, interpolated curve through a series of points.
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
 * @param {Array<Object>} points - An array of points with {x, y} properties.
 * @param {number} [smoothing=0.2] - A value to control the curve's tension.
 */
export function drawInterpolatedCurve(ctx, points, smoothing = 0.2) {
    if (points.length < 2) return;

    // For 2 points, just draw a straight line.
    if (points.length === 2) {
        ctx.moveTo(points[0].x, points[0].y);
        ctx.lineTo(points[1].x, points[1].y);
        return;
    }

    // This algorithm calculates control points for cubic Bezier curves
    // to create a smooth line that passes through all the given data points.
    const controlPoints = [];
    for (let i = 0; i < points.length - 1; i++) {
        // Points for the current segment
        const p0 = i > 0 ? points[i - 1] : points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = i < points.length - 2 ? points[i + 2] : p2;

        // Calculate tangent vectors, scaled by the smoothing factor.
        // The tangent at a point is influenced by its neighbors.
        const tangent1_x = (p2.x - p0.x) * smoothing;
        const tangent1_y = (p2.y - p0.y) * smoothing;
        const tangent2_x = (p3.x - p1.x) * smoothing;
        const tangent2_y = (p3.y - p1.y) * smoothing;

        // The first control point is based on the tangent at p1.
        const cp1 = {
            x: p1.x + tangent1_x,
            y: p1.y + tangent1_y,
        };
        // The second control point is based on the tangent at p2.
        const cp2 = {
            x: p2.x - tangent2_x,
            y: p2.y - tangent2_y,
        };
        controlPoints.push({cp1, cp2});
    }

    // Draw the series of Bezier curves
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 0; i < controlPoints.length; i++) {
        const {cp1, cp2} = controlPoints[i];
        const endPoint = points[i + 1];
        ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, endPoint.x, endPoint.y);
    }
}


/**
 * Draws the temperature chart on a canvas element.
 * @param {HTMLCanvasElement} canvas - The canvas to draw on.
 * @param {object} day - The data for the specific day.
 * @param {number} wMin - The minimum temperature for the week (for y-axis scaling).
 * @param {number} wMax - The maximum temperature for the week.
 * @param {Array<Object>} daily - The daily data for the week.
 * @param {number} expandedDayIndex - The index of the expanded day.
 */
export function drawTempChart(canvas, day, wMin, wMax, daily, expandedDayIndex, { hideEarlyMorning, temperatureUnits }) {
    const ctx = canvas.getContext('2d');

    // --- 1. SETUP & CONFIGURATION ---
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;

    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    ctx.scale(dpr, dpr);

    // Drawing Style Constants (in CSS pixels)
    const Y_PADDING = 18;
    const LINE_WIDTH = 2;
    const POINT_RADIUS = 4;
    const POINT_STROKE_WIDTH = 1.5;
    const FONT_SIZE = 12;

    // --- 2. DATA & SCALING ---
    const now = new Date().getTime() / 1000;
    const oneHourAgo = now - 3600;

    let dayStart = day.time;
    let dayLength = 24 * 60 * 60;

    if (hideEarlyMorning) {
        dayStart += EARLY_MORNING_CUTOFF_HOUR * 60*60;
        dayLength = (day.time + (24 * 60 * 60)) - dayStart; // Recalculate length based on new start
    }

    const pts = day.tempData.filter(p => p.time >= Math.max(oneHourAgo, dayStart));

    // If no points are recent enough to be drawn, clear the canvas and stop.
    if (pts.length === 0) {
        ctx.clearRect(0, 0, cssWidth, cssHeight);
        return;
    }

    const tempMin = Math.min(wMin, ...pts.map(p => p.temperature));
    const tempMax = Math.max(wMax, ...pts.map(p => p.temperature));
    const tempRange = Math.max(1, tempMax - tempMin);

    const paddedHeight = cssHeight - Y_PADDING * 2;

    const scaleX = time => ((time - dayStart) / dayLength) * cssWidth;
    const scaleY = temp => Y_PADDING + paddedHeight - ((temp - tempMin) / tempRange * paddedHeight);

    const currentDayPoints = pts.map(p => ({
        x: scaleX(p.time),
        y: scaleY(p.temperature),
        temp: p.temperature,
    }));

    let extendedPoints = [...currentDayPoints];
    const currentDayIndex = expandedDayIndex;

    if (currentDayIndex > 0) {
        const prevDay = daily[currentDayIndex - 1];
        if (prevDay && prevDay.tempData && prevDay.tempData.length > 0) {
            const prevDayLastPoint = prevDay.tempData[prevDay.tempData.length - 1];
            const xPrev = scaleX(prevDayLastPoint.time);
            const yPrev = scaleY(prevDayLastPoint.temperature);
            extendedPoints.unshift({x: xPrev, y: yPrev, temp: prevDayLastPoint.temperature});

        }
    }

    if (currentDayIndex < daily.length - 1) {
        const nextDay = daily[currentDayIndex + 1];
        if (nextDay && nextDay.tempData && nextDay.tempData.length > 0) {
            const nextDayFirstPoint = nextDay.tempData[0];
            const xNext = scaleX(nextDayFirstPoint.time);
            const yNext = scaleY(nextDayFirstPoint.temperature);
            extendedPoints.push({x: xNext, y: yNext, temp: nextDayFirstPoint.temperature});

        }
    }

    // --- 3. DRAWING ---
    ctx.clearRect(0, 0, cssWidth, cssHeight);
    const chartColor = getComputedStyle(canvas).getPropertyValue('--color-temperature').trim();

    // Draw smooth interpolated curve using extendedPoints
    if (extendedPoints.length > 1) {
        ctx.strokeStyle = chartColor;
        ctx.lineWidth = LINE_WIDTH;
        ctx.beginPath();
        // Call the new helper function to draw the curve
        drawInterpolatedCurve(ctx, extendedPoints);
        ctx.stroke();
    }

    // Draw points and labels for the current day's actual points only
    ctx.textAlign = 'center';
    ctx.font = `${FONT_SIZE}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;

    currentDayPoints.forEach(p => {
        if (p.x !== 0) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, POINT_RADIUS, 0, 2 * Math.PI);
            ctx.fillStyle = getComputedStyle(canvas).getPropertyValue('--color-surface').trim();
            ctx.fill();
            ctx.strokeStyle = chartColor;
            ctx.lineWidth = POINT_STROKE_WIDTH;
            ctx.stroke();

            const label = formatTemp(p.temp, temperatureUnits);
            const labelY = p.y - POINT_RADIUS - POINT_STROKE_WIDTH - 4;
            ctx.fillStyle = getComputedStyle(canvas).getPropertyValue('--color-primary').trim();
            ctx.fillText(label, p.x, labelY);
        }
    });
}